<template>
  <div class="application-component strategic-alignment-component">
    <div class="application-component-title">战略匹配</div>
    <div
      class="application-component-content"
      :style="{ fontSize }"
      v-loading="loading"
      :element-loading-text="loadingText"
    >
      <div v-if="readOnly && !resultData" class="placeholder">
        <el-skeleton :rows="6" animated />
      </div>
      <template v-else>
        <div v-if="!hasDependencies" class="dependency-tip">
          <div class="tip-title">战略匹配依赖 STEP1 技术概览和特征拆解结果</div>
          <div class="tip-desc">请先完成 STEP1 审核并等待“特征拆解”生成完成，系统再识别技术领域并输出战略维度匹配表。</div>
        </div>

        <template v-else>
          <div class="panel-head">
            <div class="panel-main">
              <div class="panel-title-row">
                <div class="panel-title">技术领域：{{ resultData?.domainLabel || '待识别' }}</div>
                <div :class="['domain-badge', { 'is-other': resultData && resultData.presetDomainMatched === false }]">
                  {{ resultData?.domainCode || '--' }}
                </div>
              </div>
              <div class="panel-meta">基于 STEP1 技术领域与特征拆解结果自动识别战略领域并生成匹配表。</div>
              <div v-if="resultData?.fieldText" class="field-chip">STEP1 技术领域：{{ resultData.fieldText }}</div>
              <div v-if="resultData?.domainReason" class="domain-reason">{{ resultData.domainReason }}</div>
            </div>
            <div v-if="!readOnly" class="panel-actions">
              <el-button size="small" @click="handleGenerate()">重新生成</el-button>
            </div>
          </div>

          <div v-if="sourceStale && !loading" class="stale-tip">
            <span>上游分析结果已更新，当前战略匹配结果等待重新生成。</span>
          </div>

          <div v-if="resultData && resultData.presetDomainMatched === false" class="empty-state">
            <div class="empty-state-title">其他技术领域</div>
            <div class="empty-state-desc">{{ resultData?.message || '未匹配到预设领域，暂不进行战略维度分析。' }}</div>
          </div>

          <template v-else>
            <div v-if="rows.length" class="alignment-table-scroll">
              <table class="alignment-table">
                <thead>
                  <tr>
                    <th class="dimension-column">战略维度</th>
                    <th class="level-column">匹配度</th>
                    <th class="reason-column">匹配说明</th>
                    <th class="suggestion-column">战略贴近建议</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in rows"
                    :key="row.dimensionCode"
                    class="alignment-row"
                    @click="handleFocusRow(row)"
                  >
                    <td class="dimension-column">
                      <div class="dimension-card">
                        <div class="dimension-code">{{ row.dimensionCode }}</div>
                        <div class="dimension-label">{{ row.dimensionLabel }}</div>
                        <div class="dimension-meta" :class="{ empty: !buildEvidenceSummary(row) }">
                          {{ buildEvidenceSummary(row) }}
                        </div>
                      </div>
                    </td>
                    <td class="level-column">
                      <div class="cell-card compact" @click.stop>
                        <el-select
                          v-model="row.matchLevel"
                          class="cell-select"
                          :disabled="readOnly"
                          @change="handleMatchLevelChange(row)"
                        >
                          <el-option
                            v-for="option in matchLevelOptions"
                            :key="option.value"
                            :label="option.label"
                            :value="option.value"
                          />
                        </el-select>
                        <div class="match-preview" :class="getMatchLevelClass(row.matchLevel)">
                          {{ row.matchLevelLabel }}
                        </div>
                      </div>
                    </td>
                    <td class="reason-column">
                      <div class="cell-card" @click.stop>
                        <el-input
                          v-model="row.reason"
                          type="textarea"
                          :autosize="{ minRows: 3, maxRows: 6 }"
                          resize="none"
                          :disabled="readOnly"
                          @blur="handleRowBlur"
                        />
                      </div>
                    </td>
                    <td class="suggestion-column">
                      <div class="cell-card" @click.stop>
                        <el-input
                          v-model="row.suggestion"
                          type="textarea"
                          :autosize="{ minRows: 3, maxRows: 6 }"
                          resize="none"
                          :disabled="readOnly || row.matchLevel === 'HIGH'"
                          :placeholder="row.matchLevel === 'HIGH' ? '高匹配度无需贴近建议' : '请输入战略贴近建议'"
                          @blur="handleRowBlur"
                        />
                        <div v-if="!readOnly && row.suggestion" class="suggestion-actions">
                          <el-button size="small" type="primary" plain @click="handleAddIssueToDetail(row)">
                            加入交流清单
                          </el-button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <el-empty v-else description="正在等待战略匹配结果生成" />
          </template>

          <div class="knowledge-panel">
            <div class="knowledge-title">生成原则</div>
            <div class="knowledge-grid">
              <div class="knowledge-card">
                <div class="knowledge-card-title">高匹配</div>
                <div class="knowledge-card-desc">技术核心属于该战略维度，通常不再补充战略贴近建议。</div>
              </div>
              <div class="knowledge-card">
                <div class="knowledge-card-title">中匹配</div>
                <div class="knowledge-card-desc">技术方案部分涉及该战略维度，可补充应用场景、算法模块或机制描述。</div>
              </div>
              <div class="knowledge-card">
                <div class="knowledge-card-title">低匹配</div>
                <div class="knowledge-card-desc">当前技术方案未明显涉及该战略维度，可补充相关系统模块或技术机制。</div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ElMessage } from 'element-plus'
import { computed, onUnmounted, ref, watch } from 'vue'
import { fetchGenerateStrategicAlignment, fetchSaveComponent, fetchSetp1Info } from '@/api/writing'
import { useGlobalStore } from '@/store/modules/global'
import { useWritingStore } from '@/store/modules/writing'
import { jsonParse } from '@/utils'

const COMPONENT_TYPE_CODE = 'comp_sta'
const FEATURE_BREAKDOWN_COMPONENT_CODE = 'comp_fea'
const DETAIL_LIST_COMPONENT_CODE = 'comp_comm'
const FEATURE_BREAKDOWN_EVENT = 'feature-breakdown-focus'

const MATCH_LEVEL_OPTIONS = [
  { value: 'HIGH', label: '高' },
  { value: 'MEDIUM', label: '中' },
  { value: 'LOW', label: '低' },
]

const globalStore = useGlobalStore()
const writingStore = useWritingStore()

const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => null,
  },
  projectId: {
    type: String,
    default: '',
  },
  handleAddToDetailList: {
    type: Function,
    default: null,
  },
  handleUpdateDetailList: {
    type: Function,
    default: null,
  },
  handleFocusMainFileSource: {
    type: Function,
    default: null,
  }
})

const resultData = ref(null)
const generating = ref(false)
const saving = ref(false)
const localExtOne = ref('')
const step1ResultRaw = ref('')
const step1Loaded = ref(false)
const lastAutoSourceVersion = ref('')

let autoGenerateTimer = null

const fontSize = computed(() => `${globalStore.fontSize}px`)
const loading = computed(() => generating.value || saving.value)
const loadingText = computed(() => {
  if (generating.value) {
    return '正在思考中...'
  }
  if (saving.value) {
    return '正在思考中...'
  }
  return ''
})

const featureRaw = computed(() => writingStore.step2Info?.step2Component?.[FEATURE_BREAKDOWN_COMPONENT_CODE]?.docResult || '')
const featureItems = computed(() => {
  const parsed = jsonParse(featureRaw.value, [])
  return Array.isArray(parsed) ? parsed : []
})
const hasDependencies = computed(() => featureItems.value.length > 0)
const currentSourceVersion = computed(() => {
  if (!step1Loaded.value || !hasDependencies.value) {
    return ''
  }
  return hashJavaString(`${step1ResultRaw.value || ''}||${featureRaw.value || ''}`)
})
const rows = computed(() => resultData.value?.rows || [])
const sourceStale = computed(() => {
  if (!resultData.value?.sourceVersion || !currentSourceVersion.value) {
    return false
  }
  return resultData.value.sourceVersion !== currentSourceVersion.value
})

const matchLevelOptions = MATCH_LEVEL_OPTIONS

const hashJavaString = (value = '') => {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash * 31) + value.charCodeAt(index)) | 0
  }
  return (hash >>> 0).toString(16)
}

const getMatchLevelLabel = (matchLevel) => {
  const matched = matchLevelOptions.find(item => item.value === matchLevel)
  return matched?.label || '低'
}

const normalizeSourceRanges = (sourceRanges = []) => {
  if (!Array.isArray(sourceRanges)) {
    return []
  }

  return sourceRanges
    .map((item) => {
      const paragraphIndex = Number(item?.paragraphIndex)
      const startOffset = Number(item?.startOffset)
      const endOffset = Number(item?.endOffset)

      return {
        paragraphIndex: Number.isFinite(paragraphIndex) ? paragraphIndex : null,
        anchorId: item?.anchorId || (Number.isFinite(paragraphIndex) ? `note-paragraph-${paragraphIndex}` : ''),
        startOffset: Number.isFinite(startOffset) ? startOffset : null,
        endOffset: Number.isFinite(endOffset) ? endOffset : null,
        quote: item?.quote || '',
      }
    })
    .filter(item =>
      Number.isInteger(item.paragraphIndex) &&
      Number.isInteger(item.startOffset) &&
      Number.isInteger(item.endOffset) &&
      item.endOffset > item.startOffset
    )
}

const normalizeParagraphIndices = (sourceRanges, paragraphIndices = []) => {
  return Array.from(new Set([
    ...(Array.isArray(paragraphIndices) ? paragraphIndices : []),
    ...(Array.isArray(sourceRanges) ? sourceRanges.map(item => item?.paragraphIndex) : []),
  ].map(value => Number(value)).filter(value => Number.isInteger(value))))
}

const normalizeRow = (item = {}) => {
  const sourceRanges = normalizeSourceRanges(item?.sourceRanges)
  const matchLevel = matchLevelOptions.some(option => option.value === item?.matchLevel)
    ? item.matchLevel
    : 'LOW'

  return {
    dimensionCode: item?.dimensionCode || '',
    dimensionLabel: item?.dimensionLabel || '',
    matchLevel,
    matchLevelLabel: getMatchLevelLabel(matchLevel),
    reason: item?.reason || '',
    suggestion: item?.suggestion || '',
    evidenceFeatureCodes: Array.isArray(item?.evidenceFeatureCodes) ? item.evidenceFeatureCodes.filter(Boolean) : [],
    sourceRanges,
    paragraphIndices: normalizeParagraphIndices(sourceRanges, item?.paragraphIndices),
  }
}

const normalizeResult = (value) => {
  const parsed = typeof value === 'string' ? jsonParse(value, null) : value
  if (!parsed || typeof parsed !== 'object') {
    return null
  }

  const rowList = Array.isArray(parsed.rows) ? parsed.rows.map(normalizeRow) : []
  return {
    sourceVersion: parsed.sourceVersion || '',
    updatedAt: parsed.updatedAt || '',
    fieldText: parsed.fieldText || '',
    domainCode: parsed.domainCode || '',
    domainLabel: parsed.domainLabel || '',
    presetDomainMatched: parsed.presetDomainMatched !== false,
    domainReason: parsed.domainReason || '',
    message: parsed.message || '',
    rowCount: Number(parsed.rowCount ?? rowList.length),
    rows: rowList,
  }
}

const syncStepState = (nextResult, extOne = localExtOne.value || props.data?.extOne || '') => {
  const normalized = normalizeResult(nextResult)
  if (!normalized) {
    return
  }

  resultData.value = normalized
  writingStore.setStep2Info({
    ...(writingStore.step2Info || {}),
    step2Component: {
      ...(writingStore.step2Info?.step2Component || {}),
      [COMPONENT_TYPE_CODE]: {
        ...(writingStore.step2Info?.step2Component?.[COMPONENT_TYPE_CODE] || {}),
        ...(props.data || {}),
        docResult: JSON.stringify(normalized),
        extOne,
      }
    }
  })
}

const persistResult = async (nextResult) => {
  const normalized = normalizeResult(nextResult)
  if (!normalized || !props.projectId) {
    return
  }

  saving.value = true
  try {
    await fetchSaveComponent({
      disclosureProjectId: props.projectId,
      componentTypeCode: COMPONENT_TYPE_CODE,
      docResult: JSON.stringify(normalized),
      extOne: localExtOne.value || props.data?.extOne || '',
    })
    syncStepState(normalized)
  } finally {
    saving.value = false
  }
}

const applyBackendResult = (result, successText = '') => {
  const normalized = normalizeResult(result)
  if (!normalized) {
    throw new Error('invalid strategic alignment result')
  }

  syncStepState(normalized)
  if (successText) {
    ElMessage.success(successText)
  }
}

const loadStep1Result = async () => {
  if (!props.projectId) {
    step1ResultRaw.value = ''
    step1Loaded.value = true
    return
  }

  step1Loaded.value = false
  try {
    const result = await fetchSetp1Info(props.projectId)
    step1ResultRaw.value = result?.step1Result || ''
  } catch (error) {
    console.error('load step1 result failed', error)
    step1ResultRaw.value = ''
  } finally {
    step1Loaded.value = true
  }
}

const handleGenerate = async ({ auto = false } = {}) => {
  if (!props.projectId || !hasDependencies.value || generating.value) {
    return
  }

  generating.value = true
  try {
    const result = await fetchGenerateStrategicAlignment(props.projectId)
    applyBackendResult(result, auto ? '' : '战略匹配已更新')
  } catch (error) {
    console.error('generate strategic alignment failed', error)
    ElMessage.error(auto ? '战略匹配自动更新失败' : '战略匹配生成失败')
  } finally {
    generating.value = false
  }
}

const scheduleAutoGenerate = () => {
  if (!step1Loaded.value || !hasDependencies.value || props.readOnly || generating.value || !currentSourceVersion.value) {
    return
  }

  if (resultData.value?.sourceVersion === currentSourceVersion.value && rows.value.length) {
    return
  }

  if (lastAutoSourceVersion.value === currentSourceVersion.value) {
    return
  }

  if (autoGenerateTimer) {
    clearTimeout(autoGenerateTimer)
  }

  autoGenerateTimer = setTimeout(async () => {
    lastAutoSourceVersion.value = currentSourceVersion.value
    await handleGenerate({ auto: true })
  }, 400)
}

const buildEvidenceSummary = (row) => {
  const summaryParts = []
  if (Array.isArray(row?.evidenceFeatureCodes) && row.evidenceFeatureCodes.length) {
    summaryParts.push(`关联特征：${row.evidenceFeatureCodes.join('、')}`)
  }
  if (Array.isArray(row?.paragraphIndices) && row.paragraphIndices.length) {
    summaryParts.push(`关联段落：第 ${row.paragraphIndices.join('、')} 段`)
  }
  return summaryParts.join('；')
}

const buildFallbackSuggestion = (row) => {
  if (row?.matchLevel === 'HIGH') {
    return ''
  }
  if (row?.matchLevel === 'MEDIUM') {
    return `可在不改变核心发明的前提下，补充${row.dimensionLabel}相关应用场景、算法模块或系统机制描述。`
  }
  return `可在不改变核心发明的前提下，补充${row.dimensionLabel}相关应用场景、算法模块、技术机制或系统模块，以增强该方向的战略贴近度。`
}

const handleFocusRow = (row) => {
  if (!Array.isArray(row?.sourceRanges) || !row.sourceRanges.length) {
    ElMessage.warning('当前维度未关联原文片段，请重新生成战略匹配')
    return
  }

  if (props.handleFocusMainFileSource) {
    props.handleFocusMainFileSource({
      itemId: row?.dimensionCode || '',
      sourceRanges: row.sourceRanges,
    })
    return
  }

  window.dispatchEvent(new CustomEvent(FEATURE_BREAKDOWN_EVENT, {
    detail: {
      projectId: props.projectId,
      sourceRanges: row.sourceRanges,
    }
  }))
}

const handleMatchLevelChange = async (row) => {
  row.matchLevelLabel = getMatchLevelLabel(row.matchLevel)
  if (row.matchLevel === 'HIGH') {
    row.suggestion = ''
  } else if (!row.suggestion) {
    row.suggestion = buildFallbackSuggestion(row)
  }

  try {
    await persistResult({
      ...(resultData.value || {}),
      rows: rows.value,
    })
  } catch (error) {
    console.error('save strategic alignment level failed', error)
    ElMessage.error('战略匹配保存失败')
  }
}

const handleRowBlur = async () => {
  try {
    await persistResult({
      ...(resultData.value || {}),
      rows: rows.value,
    })
  } catch (error) {
    console.error('save strategic alignment failed', error)
    ElMessage.error('战略匹配保存失败')
  }
}

const buildDetailItem = (row) => {
  return {
    type: 'text',
    sourceId: `strategic-alignment:${resultData.value?.domainCode || 'Z'}:${row.dimensionCode}`,
    question: `[战略维度优化] ${row.dimensionCode} ${row.dimensionLabel}`,
    answer: [
      `技术领域：${resultData.value?.domainLabel || '未识别'}`,
      `战略维度：${row.dimensionCode} ${row.dimensionLabel}`,
      `匹配度：${row.matchLevelLabel}`,
      `匹配说明：${row.reason || '暂无'}`,
      row.suggestion ? `建议：${row.suggestion}` : '',
    ].filter(Boolean).join('\n')
  }
}

const handleAddIssueToDetail = (row) => {
  if (!row?.suggestion) {
    ElMessage.warning('当前维度暂无可加入的战略建议')
    return
  }

  const nextItem = buildDetailItem(row)

  if (props.handleUpdateDetailList) {
    const currentList = jsonParse(
      writingStore.step2Info?.step2Component?.[DETAIL_LIST_COMPONENT_CODE]?.docResult || '[]',
      []
    )
    const nextList = Array.isArray(currentList) ? [...currentList] : []
    const targetIndex = nextList.findIndex(item => item?.sourceId === nextItem.sourceId)
    if (targetIndex === -1) {
      nextList.push(nextItem)
    } else {
      nextList[targetIndex] = nextItem
    }
    props.handleUpdateDetailList(nextList)
  } else if (props.handleAddToDetailList) {
    props.handleAddToDetailList(nextItem)
  } else {
    return
  }

  ElMessage.success('已加入交流清单')
}

const getMatchLevelClass = (matchLevel) => {
  if (matchLevel === 'HIGH') {
    return 'is-high'
  }
  if (matchLevel === 'MEDIUM') {
    return 'is-medium'
  }
  return 'is-low'
}

watch(
  () => props.data?.docResult,
  (newValue) => {
    resultData.value = normalizeResult(newValue)
  },
  { immediate: true }
)

watch(
  () => props.data?.extOne,
  (newValue) => {
    localExtOne.value = newValue || ''
  },
  { immediate: true }
)

watch(
  () => props.projectId,
  async () => {
    step1ResultRaw.value = ''
    step1Loaded.value = false
    lastAutoSourceVersion.value = ''
    if (autoGenerateTimer) {
      clearTimeout(autoGenerateTimer)
      autoGenerateTimer = null
    }
    await loadStep1Result()
  },
  { immediate: true }
)

watch(
  () => `${step1ResultRaw.value}||${featureRaw.value}`,
  () => {
    scheduleAutoGenerate()
  },
  { immediate: true }
)

onUnmounted(() => {
  if (autoGenerateTimer) {
    clearTimeout(autoGenerateTimer)
  }
})
</script>

<style scoped lang="scss">
@import './common.scss';

.dependency-tip,
.stale-tip,
.panel-head,
.knowledge-panel,
.empty-state {
  padding: 14px 16px;
  border-radius: 14px;
}

.dependency-tip {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #dbe4ee;
}

.tip-title,
.panel-title,
.knowledge-title,
.empty-state-title {
  color: #111827;
  font-weight: 600;
}

.tip-desc,
.panel-meta,
.domain-reason,
.empty-state-desc {
  margin-top: 6px;
  color: #4b5563;
  line-height: 1.7;
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.panel-main {
  flex: 1;
}

.panel-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.panel-meta {
  margin-top: 4px;
  font-size: 12px;
}

.field-chip,
.domain-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
}

.field-chip {
  margin-top: 10px;
  padding: 5px 10px;
  font-size: 12px;
  color: #14532d;
  background: #f0fdf4;
}

.domain-badge {
  min-width: 34px;
  height: 26px;
  padding: 0 10px;
  color: #166534;
  font-size: 12px;
  font-weight: 600;
  background: #dcfce7;

  &.is-other {
    color: #92400e;
    background: #fef3c7;
  }
}

.panel-actions {
  flex-shrink: 0;
}

.stale-tip {
  margin-top: 14px;
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.empty-state {
  margin-top: 16px;
  background: linear-gradient(180deg, #fffaf0 0%, #ffffff 100%);
  border: 1px dashed #fbbf24;
}

.empty-state-desc {
  margin-top: 8px;
}

.alignment-table-scroll {
  margin-top: 16px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
}

.alignment-table {
  width: 100%;
  min-width: 1040px;
  border-collapse: collapse;

  th,
  td {
    padding: 14px 12px;
    vertical-align: top;
    border-bottom: 1px solid #eef2f7;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    color: #475569;
    font-size: 12px;
    font-weight: 600;
    text-align: left;
    background: #f8fafc;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.alignment-row {
  transition: background 0.2s ease;

  &:hover {
    background: #fafafa;
  }
}

.dimension-column {
  width: 240px;
  min-width: 240px;
}

.level-column {
  width: 170px;
  min-width: 170px;
}

.reason-column,
.suggestion-column {
  min-width: 320px;
}

.dimension-card,
.cell-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

.dimension-card {
  min-height: 122px;
}

.dimension-code {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  min-width: 48px;
  height: 28px;
  padding: 0 10px;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  background: #f3f4f6;
  border-radius: 999px;
}

.dimension-label {
  color: #111827;
  font-weight: 600;
  line-height: 1.6;
}

.dimension-meta {
  font-size: 12px;
  color: #0f766e;
  line-height: 1.7;

  &.empty {
    color: #9ca3af;
  }
}

.cell-card.compact {
  min-height: 122px;
}

.cell-select {
  width: 100%;
}

.match-preview {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: #111827;

  &.is-high {
    color: #166534;
    background: #dcfce7;
  }

  &.is-medium {
    color: #92400e;
    background: #fef3c7;
  }

  &.is-low {
    color: #991b1b;
    background: #fee2e2;
  }
}

.suggestion-actions {
  display: flex;
  justify-content: flex-end;
}

.knowledge-panel {
  margin-top: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbf9 100%);
  border: 1px solid #e5e7eb;
}

.knowledge-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.knowledge-card {
  padding: 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #16a34a;
  border-radius: 12px;
}

.knowledge-card-title {
  color: #111827;
  font-weight: 600;
}

.knowledge-card-desc {
  margin-top: 6px;
  color: #4b5563;
  line-height: 1.7;
}

.placeholder {
  :deep(.el-skeleton__item) {
    border-radius: 8px;
  }
}

:deep(.el-textarea__inner) {
  min-height: 72px !important;
}

@media (max-width: 1280px) {
  .knowledge-grid {
    grid-template-columns: 1fr;
  }
}
</style>
